import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, DateRangePicker, Select, Timestamp, WebhookEvent, WebhookNotificationStatus } from 'vopay';
import { Button } from '#shared/components/UI';
import styles from './WebhooksHistory.module.scss';

type WebhooksSearchParams = {
  eventType: string;
  notificationStatus: WebhookNotificationStatus | 'all';
  timeRange: [Timestamp, Timestamp];
};

type SearchFormProps = {
  events: readonly WebhookEvent[];
  /** False hides the Status filter: the endpoint can't filter by notification status (see WebhooksHostProps). */
  hasDeliveryDetails: boolean;
  shouldShowEndDateError: boolean;
  hasInvalidEndDate?: boolean;
  eventType: string;
  notificationStatus: WebhookNotificationStatus | 'all';
  initialTimeRange: [Timestamp, Timestamp];
  onShowEndDateErrorChange: (show: boolean) => void;
  search: (searchParams: WebhooksSearchParams) => void;
};

export default function WebhooksHistorySearchForm({
  events,
  hasDeliveryDetails,
  hasInvalidEndDate,
  shouldShowEndDateError,
  notificationStatus,
  eventType,
  initialTimeRange,
  onShowEndDateErrorChange,
  search,
}: SearchFormProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Webhooks.History.SearchForm' });
  const { t: tErrors } = useTranslation('translations', { keyPrefix: 'Shared.Webhooks.History.ErrorMessages' });
  const [currentEventType, setCurrentEventType] = useState<string>(eventType);

  const [currentNotificationStatus, setCurrentNotificationStatus] = useState<WebhookNotificationStatus | 'all'>(
    notificationStatus,
  );

  const [timeRange, setTimeRange] = useState<[Timestamp, Timestamp]>(initialTimeRange);
  const [webhooksHistorySearchError, hasWebhooksHistorySearchError] = useState('');

  const allEventTypes = [
    { value: 'all', label: t('Options.EventTypeOptions.all', { defaultValue: 'All' }) },
    {
      value: 'accountverifications',
      label: t('Options.EventTypeOptions.accountVerifications', { defaultValue: 'Account Verifications' }),
    },
    { value: 'bankaccount', label: t('Options.EventTypeOptions.bankAccount', { defaultValue: 'Bank Account' }) },
    { value: 'batchdetail', label: t('Options.EventTypeOptions.batchDetail', { defaultValue: 'Batch Detail' }) },
    { value: 'batchrequest', label: t('Options.EventTypeOptions.batchRequest', { defaultValue: 'Batch Request' }) },
    { value: 'elinx', label: t('Options.EventTypeOptions.paylink', { defaultValue: 'eLinx' }) },
    {
      value: 'scheduledtransactions',
      label: t('Options.EventTypeOptions.scheduledTransactions', { defaultValue: 'Scheduled Transactions' }),
    },
    { value: 'transaction', label: t('Options.EventTypeOptions.transaction', { defaultValue: 'Transaction' }) },
    {
      value: 'transactiongroup',
      label: t('Options.EventTypeOptions.transactionGroup', { defaultValue: 'Transaction Group' }),
    },
    {
      value: 'accountonboarding',
      label: t('Options.EventTypeOptions.accountStatus', { defaultValue: 'Account Status' }),
    },
    {
      value: 'accountbalance',
      label: t('Options.EventTypeOptions.accountBalance', { defaultValue: 'Account Balance' }),
    },
    {
      value: 'clientaccountbalance',
      label: t('Options.EventTypeOptions.clientAccountBalance', { defaultValue: 'Client Account Balance' }),
    },
    {
      value: 'paymentreceived',
      label: t('Options.EventTypeOptions.paymentReceived', { defaultValue: 'Payment Received' }),
    },
    { value: 'accountlimit', label: t('Options.EventTypeOptions.accountLimit', { defaultValue: 'Account Limit' }) },
    {
      value: 'virtualtransaction',
      label: t('Options.EventTypeOptions.virtualAccounts', { defaultValue: 'Virtual Accounts' }),
    },
    {
      value: 'creditcard',
      label: t('Options.EventTypeOptions.creditCardConnection', { defaultValue: 'Credit Card Connection' }),
    },
    {
      value: 'debitcard',
      label: t('Options.EventTypeOptions.debitCardConnection', { defaultValue: 'Debit Card Connection' }),
    },
  ];

  const eventTypes = allEventTypes.filter(
    (option) => option.value === 'all' || events.includes(option.value as WebhookEvent),
  );

  const notificationStatuses: { value: WebhookNotificationStatus | 'all'; label: string }[] = [
    { value: 'all', label: t('Options.StatusOptions.all', { defaultValue: 'All' }) },
    { value: 'new', label: t('Options.StatusOptions.new', { defaultValue: 'New' }) },
    { value: 'processed', label: t('Options.StatusOptions.processed', { defaultValue: 'Processed' }) },
    { value: 'failed', label: t('Options.StatusOptions.failed', { defaultValue: 'Failed' }) },
    { value: 'deprecated', label: t('Options.StatusOptions.deprecated', { defaultValue: 'Deprecated' }) },
  ];

  useEffect(() => {
    const ToDate = new Date(timeRange[1]);
    const FromDate = new Date(timeRange[0]);
    const dateDifference = Math.abs(ToDate.getTime() - FromDate.getTime());
    const daysDifference = Math.ceil(dateDifference / (1000 * 60 * 60 * 24));

    if (daysDifference >= 35) {
      hasWebhooksHistorySearchError(
        tErrors('dateRange', { defaultValue: 'Date range should not be longer than 1 month.' }),
      );
    } else {
      hasWebhooksHistorySearchError('');
    }
  }, [timeRange]);

  return (
    <Box jc="sb" flx="col" width="100%" gap="md">
      <Box gap="sm" wrap="wrap">
        <DateRangePicker
          onChange={(range) => {
            setTimeRange(range);
          }}
          initialTime={initialTimeRange}
          triggerVariant="outlineShadow"
          hasInvalidEndDate={hasInvalidEndDate}
          shouldShowEndDateError={shouldShowEndDateError}
          setShouldShowEndDateError={onShowEndDateErrorChange}
        />

        <Box width="200px">
          <Select
            version="2"
            placeholder={t('eventPlaceholder', { defaultValue: 'Event' })}
            options={eventTypes}
            name="type"
            value={currentEventType}
            sort={false}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentEventType(e.target.value);
            }}
          />
        </Box>

        {hasDeliveryDetails && (
          <Box width="200px">
            <Select
              version="2"
              placeholder={t('statusPlaceholder', { defaultValue: 'Status' })}
              options={notificationStatuses}
              name="notificationStatus"
              value={currentNotificationStatus}
              onBlur={() => {}}
              onChange={(_, { data }) => {
                if (data) setCurrentNotificationStatus(data.value);
              }}
            />
          </Box>
        )}

        <Button
          id="search-transaction-history"
          size="small"
          colorScheme="primary"
          disabled={!!webhooksHistorySearchError}
          onClick={() => {
            search({
              eventType: currentEventType,
              notificationStatus: currentNotificationStatus,
              timeRange,
            });
          }}
        >
          {t('search', { defaultValue: 'Search' })}
        </Button>
      </Box>

      {webhooksHistorySearchError !== '' && (
        <div className={styles.webhookValidationError}>{webhooksHistorySearchError}</div>
      )}
    </Box>
  );
}
